/** React bindings for the offline store + Home-Screen gating. */
import { useSyncExternalStore } from 'react';
import { isIOS, isStandalone } from '../lib/standalone';
import { getSnapshot, subscribe, type OfflineSnapshot, type PackView, type SongOfflineState } from './store';
import { packId, type PackKind } from './types';

export function useOfflineSnapshot(): OfflineSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useSongOfflineState(songId: string): SongOfflineState | null {
  return useSyncExternalStore(
    subscribe,
    () => getSnapshot().songs.get(songId) ?? null,
    () => null,
  );
}

export function usePack(kind: PackKind, refId: string | null): PackView | null {
  const id = packId(kind, refId);
  return useSyncExternalStore(
    subscribe,
    () => getSnapshot().packs.find((p) => p.row.id === id) ?? null,
    () => null,
  );
}

/**
 * Saving for offline is offered everywhere except iOS/iPadOS Safari tabs: there the copies would be
 * invisible to the installed Home-Screen app and Safari evicts them after 7 days without a visit.
 */
export function canSaveOffline(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof caches === 'undefined') return false;
  return !isIOS() || isStandalone();
}

/** True when saving is hidden only because the app runs in an iOS Safari tab (show the install hint). */
export function needsInstallForOffline(): boolean {
  return typeof window !== 'undefined' && isIOS() && !isStandalone();
}
